import { Chart, formatCurrency, getColorFromCssVariable } from "ui";
import { ChartCard } from "./ChartCard";
import { useDatadogQuery } from "./useDatadogQuery";
import { useMemo } from "react";
import { formatChartLabel } from "./utils";

type RevenueData = {
  value: number;
  label: string;
}[];

export function CapturedRevenueChart({
  isSameDayRange,
}: {
  isSameDayRange: boolean;
}) {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<RevenueData>({
      chartType: "CAPTURE_REVENUE",
      delayMs: 900,
    });

  const { labels, revenueData } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0)
      return { labels: [], revenueData: [] };

    return {
      labels: data.map((item) => item.label),
      revenueData: data.map((item) => item.value),
    };
  }, [data]);

  const color = getColorFromCssVariable("--chart-brand");

  return (
    <ChartCard
      ref={ref}
      title="Captured revenue"
      description="Revenue from successfully collected payments."
      contentClassName="pr-0 pl-4"
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
    >
      <Chart
        type="line"
        labels={labels}
        datasets={[
          {
            label: "Revenue",
            data: revenueData,
            backgroundColor: color,
            borderColor: color,
          },
        ]}
        className="h-full"
      >
        <Chart.Canvas
          className="h-0 flex-1"
          formatYLabel={(value) =>
            formatCurrency({ amount: value, currency: "USD", compact: true })
          }
          formatXLabel={(value) => formatChartLabel({ value, isSameDayRange })}
        />
        <Chart.Tooltip
          valueFormatter={(value) =>
            formatCurrency({ amount: value, currency: "USD" })
          }
          labelFormatter={(value) =>
            formatChartLabel({ value, isSameDayRange })
          }
        />
      </Chart>
    </ChartCard>
  );
}
